import { find, map } from 'lodash'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Box, makeStyles, Switch, Typography } from '@material-ui/core'
import SaveAltOutlinedIcon from '@material-ui/icons/SaveAltOutlined'
import EditIcon from '@material-ui/icons/Edit'

import PageHeader from 'App/components/PageHeader'
import { SUPER_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import { ContainedButton, Loading, TableMenuButton } from 'civic-champs-shared/core/'
import { ReadOnlyQuestionSetQuestions } from 'civic-champs-shared/question-sets/components/'
import Select from 'civic-champs-shared/core/select'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'

import { useHasRole } from 'auth/hooks'
import { SurveyVersion } from 'surveys/types'
import CreateUpdateSurveyPrompt from 'surveys/components/CreateUpdateSurveyPrompt'
import { useDownloadExportedSurvey, useSurvey, useUpdateSurveyVisibility } from '../hooks/'
import { useInactiveQuestionSetPrompt } from '../../question-sets/hooks'
import { useFeatureEnabled } from 'core/feature/hooks'
import { Redirect } from 'react-router-dom'
import { useShowShowExportAnswersPrompt } from '../../question-sets/hooks/useShowExportAnswersPrompt'

const useStyles = makeStyles(() => ({
  actionsBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 24,
  },
  headerButton: {
    margin: 5,
    fontSize: 14,
  },
  toggle: {
    marginTop: 5,
  },
  toggleLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    margin: 5,
  },
  statusLabel: {
    fontWeight: 'bold',
    marginRight: 5,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
  },
  versionSelect: {
    minWidth: 160,
  },
  versionMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 4,
  },
  minimumRequiredVersion: {
    fontFamily: 'Nunito, sans-serif',
    fontSize: 14,
    lineHeight: '20px',
    color: '#2D3038',
  },
}))

export interface SurveyDetailsProps {
  match: {
    params: { id: string }
    url: string
  }
  backLink?: string
  backText?: string
  pageTitle?: string
}

interface Option {
  value: number
  label: string
}

export const SurveyDetails: FC<SurveyDetailsProps> = ({
  match,
  backLink = '/questionnaires',
  backText = 'Back to Questionnaires',
  pageTitle = 'View Questionnaire',
}) => {
  const surveyId = parseInt(match.params.id)
  const [survey, setSurvey] = useState<any>(null)
  const [redirect, setRedirect] = useState<null | string>(null)
  const [{ loading, value: surveyData }] = useSurvey(surveyId)
  const classes = useStyles()
  const [downloadExportedSurvey] = useDownloadExportedSurvey()

  const isNewQuestionnairesEditorEnabled = useFeatureEnabled('NewQuestionnairesEditor')
  const isMinimumRequiredVersionEnabled = useFeatureEnabled('QuestionnaireMinimumRequiredVersion')

  const showCreateUpdatePrompt = useShowPrompt(CreateUpdateSurveyPrompt)
  useEffect(() => {
    !loading && setSurvey(surveyData)
  }, [loading, surveyData])
  const handleEdit = useCallback(async () => {
    if (isNewQuestionnairesEditorEnabled) {
      setRedirect(`${match.url}/edit`)
    } else {
      setSurvey(await showCreateUpdatePrompt({ survey }))
    }
  }, [isNewQuestionnairesEditorEnabled, match.url, showCreateUpdatePrompt, survey])

  const [selectedVersion, setSelectedVersion] = useState<SurveyVersion | undefined>()
  const download = useCallback(
    (query: { startDate?: string; endDate?: string; mostRecentOnly?: boolean }) => {
      ;(downloadExportedSurvey as any)(survey, selectedVersion, query)
    },
    [downloadExportedSurvey, selectedVersion, survey],
  )

  const showExportPrompt = useShowShowExportAnswersPrompt(download)
  useEffect(() => {
    // @ts-ignore
    !loading && setSelectedVersion(survey?.versions[0])
  }, [loading, survey])

  const [options, value] = useMemo(() => {
    const options = map(survey?.versions, ({ version }) => ({
      value: version,
      label: `Version ${version}`,
    })) as Option[]
    const value = find(options, { value: selectedVersion?.version })
    return [options, value]
  }, [survey, selectedVersion])

  const inactiveQuestionSetPrompt = useInactiveQuestionSetPrompt()
  const [updateSurveyVisibility] = useUpdateSurveyVisibility(surveyId, setSurvey)
  const isSuperAdmin = useHasRole(SUPER_ADMIN)

  const handleVersionChange = ({ value }: Option) => {
    setSelectedVersion(find(survey?.versions, { version: value }))
  }

  const onToggleActive = async (active: boolean) => {
    if (active) {
      updateSurveyVisibility(active)
    } else {
      inactiveQuestionSetPrompt(() => updateSurveyVisibility(active))
    }
  }

  if (redirect) {
    return <Redirect to={redirect} />
  }

  if (loading) {
    return <Loading />
  }

  const { anonymous, name, questions } = selectedVersion?.questionSet || {}
  const showMinimumRequiredVersion =
    isMinimumRequiredVersionEnabled && (survey?.versions?.length ?? 0) > 1 && survey?.minimumRequiredVersion

  return (
    <PageHeader title={pageTitle} subTitle={backText} backLink={backLink} isolateContentTheme>
      <div className={classes.actionsBar}>
        {isSuperAdmin && (
          <>
            <Typography className={classes.toggleLabel} component="span">
              <Box fontWeight={'bold'} className={classes.statusLabel} component="span">
                Status:
              </Box>
              {anonymous ? 'Inactive' : 'Active'}
            </Typography>

            <Switch className={classes.toggle} checked={!anonymous} onChange={e => onToggleActive(e.target.checked)} />
          </>
        )}

        <TableMenuButton
          className={classes.headerButton}
          startIcon={<SaveAltOutlinedIcon />}
          onClick={showExportPrompt}
        >
          Export Responses
        </TableMenuButton>
        <ContainedButton startIcon={<EditIcon />} className={classes.headerButton} onClick={handleEdit}>
          Edit
        </ContainedButton>
      </div>

      <div className={classes.titleRow}>
        <Typography variant="h5">{name || 'Untitled Survey'}</Typography>
        <div className={classes.versionMeta}>
          {showMinimumRequiredVersion && (
            <Typography className={classes.minimumRequiredVersion}>
              Minimum Required Version: Version {survey.minimumRequiredVersion}
            </Typography>
          )}
          <Select className={classes.versionSelect} options={options} onChange={handleVersionChange} value={value} />
        </div>
      </div>

      <ReadOnlyQuestionSetQuestions questions={questions || []} />
    </PageHeader>
  )
}
